import {
  ChevronDownIcon,
  ChevronUpIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { RESULT_ICON } from './LeadCloseIcons.component';
import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { Tooltip } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import type { tCloseLeadRef } from './LeadClose.context';
import { closeMenuEntries, formatMoney } from './LeadClose.functions';
import { useLeadCloseAnalytics } from 'src/hooks/analytics/leadClose/useLeadCloseAnalytics.hook';
import { useLeadClose } from './useLeadClose.hook';
import './LeadCloseMenu.styles.scss';

/**
 * "Marcar resultado" in the chat bar (U13): its label is the lead's latest
 * result, its first item is "Registrar otra venta" while a sale is live and
 * "Vendido" otherwise. Built on the DS `ls-menu` the app loads.
 */
export default function LeadCloseMenu(props: {
  lead: tCloseLeadRef;
}): JSX.Element {
  const { lead } = props;
  const { t } = useTranslation('chat/chat');
  const { summaryOf, loadSummary, openForm } = useLeadClose();
  const { trackMenuOpened } = useLeadCloseAnalytics();
  const summary = summaryOf(lead.id);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (summary.status === 'idle') loadSummary(lead.id);
  }, [lead.id, summary.status, loadSummary]);

  // A chat switch closes the menu of the previous lead.
  useEffect(() => setOpen(false), [lead.id]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [open]);

  const current = summary.data?.current ?? null;

  function label(): string {
    if (summary.status === 'failed') return t('leadClose.menu.failed');
    if (summary.status !== 'ready') return t('leadClose.panel.loading');
    if (!current) return t('leadClose.menu.toggle');
    const name = t(`leadClose.result.${current.type}`);
    return current.type === 'sold' && current.amount
      ? `${name} · ${formatMoney(current.amount)}`
      : name;
  }

  function onToggle() {
    if (summary.status === 'failed') {
      loadSummary(lead.id);
      return;
    }
    // Only the opening is the gesture; a close is not a second one.
    if (!open) trackMenuOpened({ lead_id: lead.id });
    setOpen(!open);
  }

  function close() {
    setOpen(false);
    toggleRef.current?.focus();
  }

  // The keyboard of the DS menu, as DirectoryHeaderFilter does: arrows, Home
  // and End move through the items, Escape closes and returns the focus, Tab
  // leaves and closes.
  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (index: number) => {
      event.preventDefault();
      items[(index + items.length) % items.length]?.focus();
    };
    if (event.key === 'ArrowDown') go(at + 1);
    else if (event.key === 'ArrowUp') go(at - 1);
    else if (event.key === 'Home') go(0);
    else if (event.key === 'End') go(items.length - 1);
    else if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') setOpen(false);
  }

  return (
    <Tooltip
      // The tooltip would sit over the open menu.
      className={open ? 'LeadCloseMenu__Tooltip--off' : undefined}
      message={t(current ? 'chatMenu.markResultSet' : 'chatMenu.markResult')}
      position="bottom"
    >
      <div ref={rootRef} className="LeadCloseMenu">
        <button
          ref={toggleRef}
          type="button"
          className="LeadCloseMenu__Toggle ph-no-capture"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          disabled={summary.status === 'loading' || summary.status === 'idle'}
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && open) {
              event.stopPropagation();
              close();
            }
          }}
          // The form takes the focus back here when its menu item is gone.
          data-lead-close-return
        >
          {/* The prototype's mark: a flag before any result, then the result's icon in its color. */}
          {current && summary.status === 'ready' ? (
            (() => {
              const Icon = RESULT_ICON[current.type];
              return (
                <Icon
                  className={classNames(
                    'LeadCloseMenu__Mark',
                    `LeadCloseMenu__Mark--${current.type}`,
                  )}
                  aria-hidden
                />
              );
            })()
          ) : (
            <FlagIcon className="LeadCloseMenu__Mark" aria-hidden />
          )}
          <span className="LeadCloseMenu__Label">{label()}</span>
          {open ? (
            <ChevronUpIcon className="LeadCloseMenu__Icon" aria-hidden />
          ) : (
            <ChevronDownIcon className="LeadCloseMenu__Icon" aria-hidden />
          )}
        </button>
        {open && summary.status === 'ready' ? (
          <div className="ls-menu ls-menu-open LeadCloseMenu__Menu">
            <div
              id={menuId}
              role="menu"
              className="ls-menu-panel"
              onKeyDown={onMenuKeyDown}
            >
              {closeMenuEntries(summary.data).map((entry) => (
                <button
                  key={entry.label}
                  type="button"
                  role="menuitem"
                  className="ls-menu-item"
                  onClick={() => {
                    setOpen(false);
                    openForm({ lead, type: entry.type, source: 'chat_bar' });
                  }}
                >
                  {(() => {
                    const Icon = RESULT_ICON[entry.type];
                    return (
                      <Icon className="LeadCloseMenu__ItemIcon" aria-hidden />
                    );
                  })()}
                  <span className="ls-menu-item-label">
                    {t(`leadClose.menu.${entry.label}`)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
}
